/**
 * json-to-scad.ts
 *
 * Converts a parsed JSON value into an OpenSCAD function definition that can
 * be inlined into a SCAD file as a replacement for `include <name.json>`.
 *
 * Supported input shapes and the function signature they produce:
 *
 *   Array<object>   → function name(index, key)          (with optional subkey for 2-level nesting)
 *   Array<scalar>   → function name(index)
 *   object (flat)   → function name(key)
 *
 * A companion `<name>_len()` function is always emitted so callers can loop
 * without hardcoding the array length:
 *
 *   for (i = [0 : name_len() - 1]) { ... }
 *
 * Scalar SCAD values:
 *   string  → "value"
 *   number  → value (integer or float)
 *   boolean → true / false
 *   null    → undef
 *
 * Nested objects at depth > 2 are skipped with a SCAD comment warning.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ScadValue = string | number | boolean | null | undefined;

export interface JsonToScadResult {
  /** The generated SCAD source block (one or more function definitions). */
  scad: string;
  /** Human-readable warnings about data that could not be converted. */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Value serialisation
// ---------------------------------------------------------------------------

/**
 * Serialise a scalar JSON value to its OpenSCAD literal representation.
 * Returns `null` when the value cannot be represented (e.g. array, object).
 */
function toScadLiteral(value: unknown): string | null {
  if (value === null || value === undefined) return 'undef';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    // Emit integers without decimal point, floats with full precision.
    return Number.isInteger(value) ? String(value) : String(value);
  }
  if (typeof value === 'string') {
    // Escape backslashes and double-quotes inside the string.
    const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  // Arrays and objects cannot be directly represented as scalars.
  return null;
}

// ---------------------------------------------------------------------------
// Array-of-objects → function name(index, key [, subkey])
// ---------------------------------------------------------------------------

/**
 * Build a ternary chain for an array of objects.
 * Supports one level of sub-object nesting (2 levels total).
 */
function buildArrayOfObjectsFunction(
  name: string,
  arr: Record<string, unknown>[],
  warnings: string[],
): string {
  const lines: string[] = [];

  // Collect all top-level keys (preserve insertion order, deduplicated).
  const topKeys = Array.from(new Set(arr.flatMap((item) => Object.keys(item))));

  // Detect which keys hold sub-objects (depth 2).
  const subObjectKeys = topKeys.filter((k) =>
    arr.some(
      (item) =>
        item[k] !== null &&
        typeof item[k] === 'object' &&
        !Array.isArray(item[k]),
    ),
  );

  // Decide if we need the subkey parameter.
  const needsSubkey = subObjectKeys.length > 0;
  const signature = needsSubkey
    ? `function ${name}(index, key, subkey = undef) =`
    : `function ${name}(index, key) =`;

  const arms: string[] = [];

  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];

    for (const key of topKeys) {
      const rawValue = item[key];

      if (
        rawValue !== null &&
        typeof rawValue === 'object' &&
        !Array.isArray(rawValue)
      ) {
        // Sub-object: emit one arm per sub-key.
        const sub = rawValue as Record<string, unknown>;
        for (const subKey of Object.keys(sub)) {
          const lit = toScadLiteral(sub[subKey]);
          if (lit === null) {
            warnings.push(
              `${name}[${i}].${key}.${subKey}: value type not supported, skipped`,
            );
            continue;
          }
          arms.push(
            `    index == ${i} && key == "${key}" && subkey == "${subKey}" ? ${lit}`,
          );
        }
      } else if (Array.isArray(rawValue)) {
        warnings.push(
          `${name}[${i}].${key}: nested arrays not supported, skipped`,
        );
      } else {
        const lit = toScadLiteral(rawValue);
        if (lit === null) {
          warnings.push(
            `${name}[${i}].${key}: value type not supported, skipped`,
          );
          continue;
        }
        arms.push(`    index == ${i} && key == "${key}" ? ${lit}`);
      }
    }
  }

  if (arms.length === 0) {
    lines.push(`${signature} undef;`);
  } else {
    lines.push(signature);
    lines.push(
      arms
        .map((arm, idx) => arm + (idx < arms.length - 1 ? ' :' : ' :'))
        .join('\n'),
    );
    lines.push('    undef;');
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Array-of-scalars → function name(index)
// ---------------------------------------------------------------------------

function buildArrayOfScalarsFunction(
  name: string,
  arr: unknown[],
  warnings: string[],
): string {
  const arms: string[] = [];

  for (let i = 0; i < arr.length; i++) {
    const lit = toScadLiteral(arr[i]);
    if (lit === null) {
      warnings.push(`${name}[${i}]: value type not supported, skipped`);
      continue;
    }
    arms.push(`    index == ${i} ? ${lit}`);
  }

  if (arms.length === 0) {
    return `function ${name}(index) = undef;`;
  }

  const terminator = arms
    .map((arm, idx) => arm + (idx < arms.length - 1 ? ' :' : ' :'))
    .join('\n');
  return [`function ${name}(index) =`, terminator, '    undef;'].join('\n');
}

// ---------------------------------------------------------------------------
// Flat object → function name(key)
// ---------------------------------------------------------------------------

function buildFlatObjectFunction(
  name: string,
  obj: Record<string, unknown>,
  warnings: string[],
): string {
  const arms: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && typeof value === 'object') {
      warnings.push(
        `${name}.${key}: nested object/array not supported in flat-object mode, skipped`,
      );
      continue;
    }
    const lit = toScadLiteral(value);
    if (lit === null) {
      warnings.push(`${name}.${key}: value type not supported, skipped`);
      continue;
    }
    arms.push(`    key == "${key}" ? ${lit}`);
  }

  if (arms.length === 0) {
    return `function ${name}(key) = undef;`;
  }

  const terminator = arms
    .map((arm, idx) => arm + (idx < arms.length - 1 ? ' :' : ' :'))
    .join('\n');
  return [`function ${name}(key) =`, terminator, '    undef;'].join('\n');
}

// ---------------------------------------------------------------------------
// Length helper
// ---------------------------------------------------------------------------

function buildLengthFunction(name: string, length: number): string {
  return `function ${name}_len() = ${length};`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert a parsed JSON value to one or more OpenSCAD function definitions.
 *
 * @param name  The function name (typically derived from the JSON filename
 *              without extension, with hyphens/spaces replaced by underscores).
 * @param data  The already-parsed JSON value.
 * @returns     `{ scad, warnings }` where `scad` is the SCAD source block.
 */
export function jsonToScadFunction(
  name: string,
  data: unknown,
): JsonToScadResult {
  const warnings: string[] = [];
  const blocks: string[] = [];

  // Header comment.
  blocks.push(`// Generated from ${name}.json`);

  if (Array.isArray(data)) {
    if (data.length === 0) {
      // Empty array — emit stubs.
      blocks.push(`function ${name}(index, key) = undef;`);
      blocks.push(buildLengthFunction(name, 0));
    } else if (
      data.length > 0 &&
      data[0] !== null &&
      typeof data[0] === 'object' &&
      !Array.isArray(data[0])
    ) {
      // Array of objects.
      blocks.push(
        buildArrayOfObjectsFunction(
          name,
          data as Record<string, unknown>[],
          warnings,
        ),
      );
      blocks.push(buildLengthFunction(name, data.length));
    } else {
      // Array of scalars (or mixed — scalars take priority).
      blocks.push(buildArrayOfScalarsFunction(name, data, warnings));
      blocks.push(buildLengthFunction(name, data.length));
    }
  } else if (data !== null && typeof data === 'object') {
    // Flat top-level object.
    blocks.push(
      buildFlatObjectFunction(name, data as Record<string, unknown>, warnings),
    );
    // No _len() for flat objects — length isn't a meaningful concept here.
  } else {
    // Scalar JSON root — wrap in a no-argument function.
    const lit = toScadLiteral(data);
    blocks.push(`function ${name}() = ${lit ?? 'undef'};`);
  }

  // Emit any warnings as SCAD comments.
  if (warnings.length > 0) {
    blocks.push(warnings.map((w) => `// WARNING: ${w}`).join('\n'));
  }

  return {
    scad: blocks.join('\n') + '\n',
    warnings,
  };
}

/**
 * Derive a safe OpenSCAD identifier from a filename stem.
 * Replaces hyphens, spaces, and dots with underscores; strips leading digits.
 */
export function filenameToScadName(filename: string): string {
  // Remove extension(s).
  const stem = filename.replace(/\.[^.]+$/, '');
  // Replace non-identifier characters with underscores.
  let name = stem.replace(/[^a-zA-Z0-9_]/g, '_');
  // Strip leading digits (OpenSCAD identifiers cannot start with a digit).
  name = name.replace(/^[0-9]+/, '');
  // Collapse consecutive underscores.
  name = name.replace(/_+/g, '_');
  // Trim leading/trailing underscores.
  name = name.replace(/^_+|_+$/g, '');
  // Fallback if name is empty.
  return name || 'data';
}
